import { Client } from "@/Common/Api/Client";
import { FileType } from "@/Common/Models/FileType";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExportableFileGenerationIntent } from "@/ExportableFile/Models/enum/ExportableFileGenerationIntent";
import { ConsultationRequestGetExportableFileFetchUrlResponse } from "../ConsultationRequestGetExportableFileFetchUrlResponse";
import { ExportableFile, IExportableFile, IExportableFileLoaded } from "../ExportableFile";
import { GenerateFileOptions } from "../IGenerateFileOptions";
import { FileTypeTemporaryFile } from "./FileTypeTemporaryFile";

interface IFileTypeConsultationRequest extends IExportableFile {
    ConsultationRequestId: string;
}

class FileTypeConsultationRequest extends ExportableFile implements IFileTypeConsultationRequest {
    public canGenerateFileXcom: boolean = true;

    public ConsultationRequestId: string;

    constructor(fields: IFileTypeConsultationRequest) {
        super(FileType.CONSULTATIONREQUEST);
        Object.assign(this, fields);
        this.allowedDraggableImageType = [];
    }

    public getAttachedObject_ObjectId(): string { return this.ConsultationRequestId; }

    public getCleanCopy(): FileTypeConsultationRequest {
        let copyConstructor: IFileTypeConsultationRequest = {
            ConsultationRequestId: this.ConsultationRequestId,
            date: this.date,
            description: this.description
        };

        return new FileTypeConsultationRequest(copyConstructor);
    }

    public async getMeta(): Promise<number> {
        throw "Not implemented";
    }

    protected async generateFileIfNeededXcom(includeOverlays: boolean): Promise<IExportableFileLoaded> {
        return await this.generateFileIfNeeded({
            includeOverlays: includeOverlays,
            isExternal: true,
            exportableFileGenerationIntent: ExportableFileGenerationIntent.Preview
        });
    }

    protected async generateFileIfNeeded(generateFileOption: GenerateFileOptions): Promise<IExportableFileLoaded> {
        const { includeOverlays, isExternal } = generateFileOption;
        const requestedOverlays = (includeOverlays && this.overlayElements) || [];

        const response =
            isExternal
                ? await Client.postAsyncXcom<ConsultationRequestGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.ExportableFile_GetConsultationRequestFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(),
                        ConsultationRequestId: this.ConsultationRequestId,
                        SignedPrintAuthorization: this.hasSignature,
                        IsPreview: !generateFileOption.isFinalFile,
                        GenerationIntent: generateFileOption.exportableFileGenerationIntent
                    },
                    this.clientTag,
                    this.claimId
                )
                : await Client.postAsync<ConsultationRequestGetExportableFileFetchUrlResponse>(
                    ServerOperations.Server.ExportableFile_GetConsultationRequestFetchUrl,
                    {
                        OverlayElements: this.serializeOverlayElements(),
                        ConsultationRequestId: this.ConsultationRequestId,
                        SignedPrintAuthorization: this.hasSignature,
                        IsPreview: !generateFileOption.isFinalFile,
                        GenerationIntent: generateFileOption.exportableFileGenerationIntent
                    }
                );

        if (!response.isSuccess)
            return;

        if (response.data.NextStatusLookupId)
            this.updateRequestStatus(response.data.NextStatusLookupId);

        this.totalPages = response.data.TotalPages;

        const tempFile = new FileTypeTemporaryFile({
            ObjectId: response.data.ObjectId,
            totalPages: this.totalPages,
            sizeBytes: response.data.SizeBytes,
            date: this.date,
            description: this.description
        });

        tempFile.isExternal = isExternal;
        if (tempFile.isExternal) {
            tempFile.clientTag = this.clientTag;
            tempFile.claimId = this.claimId;
        }

        super.setCache(tempFile, requestedOverlays);

        return {
            OriginalExportableFile: this,
            PregeneratedTemporaryFile: tempFile,
            FetchUrl:
                isExternal
                    ? tempFile.FetchUrlXcom(this.clientTag, this.claimId)
                    : tempFile.FetchUrl
        } as IExportableFileLoaded;
    }

    public async persistSignature(): Promise<void> {
        await Client.postAsync<{ SignedPrintAuthorization: SignedPrintAuthorization }>(
            ServerOperations.Server.Myle_SaveSignedPrintAuthorization,
            { ConsultationRequestId: this.ConsultationRequestId }
        );
    }

    private updateRequestStatus(nextStatusLookupId: string): void {
        if (!cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements)
            return;

        const patientContactId = patientStore.getCurrentPatientContactId();
        const patientNoteModelConsultationRequests = patientStore.PatientMedicalNoteModels[patientContactId]?.get("ConsultationRequests");
        const patientMedicalSummaryModelConsultationRequests = patientStore.PatientMedicalSummaryModels[patientContactId]?.get("ConsultationRequests");

        if (patientNoteModelConsultationRequests) {
            const consultationRequest = patientNoteModelConsultationRequests
                .find(p => p.ConsultationRequestId === this.ConsultationRequestId);

            if (consultationRequest) {
                consultationRequest.StatusLookupId = nextStatusLookupId;
                const nextConsultationRequests = patientNoteModelConsultationRequests
                    .upsert(obj => obj.ConsultationRequestId === this.ConsultationRequestId, consultationRequest);
                patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId].set("ConsultationRequests", nextConsultationRequests);
                patientStore.EmitChangeToListeners();
            }
        }

        if (patientMedicalSummaryModelConsultationRequests) {
            const consultationRequest = patientMedicalSummaryModelConsultationRequests
                .find(p => p.ConsultationRequestId === this.ConsultationRequestId);

            if (consultationRequest) {
                consultationRequest.StatusLookupId = nextStatusLookupId;
                const nextConsultationRequests = patientMedicalSummaryModelConsultationRequests
                    .upsert(obj => obj.ConsultationRequestId === this.ConsultationRequestId, consultationRequest);
                patientStore.PatientMedicalSummaryModels[patientContactId] = patientStore.PatientMedicalSummaryModels[patientContactId].set("ConsultationRequests", nextConsultationRequests);
                patientStore.ActionsMedicalSummary.reloadFollowUpScreeningData();
            }
        }
    }
}

export { FileTypeConsultationRequest };
